import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { EntityTypeEnum } from '../../../audit-log/entity-type.enum';
import { AuditLogService } from '../../../audit-log/audit-log.service';
import { ToastrService } from 'ngx-toastr';
import { IAuditLog } from '../../../audit-log/audit-log.interface';
import { ActionTypeEnum } from '../../../audit-log/action-type.enum';
import { combineLatest, Observable, Subscription} from 'rxjs';
import { LangChangeEvent, TranslateService} from '@ngx-translate/core';
import { map, tap } from 'rxjs/operators';
import { LocaleEnum } from '../../../submit-lecture/lecture.values';
import { GridOptions } from 'ag-grid-community';
import { LecturesService } from 'src/app/views/pages/lectures/lectures.service';
import { LocalStorageKeys } from 'src/app/core/local-storage/local-storage.enum';

@Component({
  selector: 'audit-logs-table',
  templateUrl: './audit-logs-table.component.html',
  styleUrls: ['./audit-logs-table.component.scss'],
})
export class AuditLogsTableComponent implements OnInit, OnChanges, OnDestroy {

  records: any[] = [];
  columns: any;
  subscription: Subscription;
  isRtl;
  private loading: boolean;
  localStorageKey: LocalStorageKeys.auditTableFilter;

  gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: 'agTextColumnFilter',
    },
    suppressHorizontalScroll: true,
    pagination: true,
    allowContextMenuWithControlKey: true,
    suppressScrollOnNewData: true,
    suppressRowClickSelection: true
  };

  // get audit logs records by entity type, if entity id was not provided
  @Input() entityType: EntityTypeEnum;

  // get audit logs records by entity id
  @Input() entityId: string;

  constructor(private readonly auditLogsService: AuditLogService,
              private readonly toastr: ToastrService,
              private readonly translate: TranslateService,
              private readonly lecturesService: LecturesService) { }

  ngOnInit(): void {
    this.isRtl = this.translate.currentLang === LocaleEnum.HE;
    this.columns = combineLatest(this.initColumnsDefs());
    this.subscription = this.translate.onLangChange.subscribe(
        (event: LangChangeEvent) => {
          this.columns = combineLatest(this.initColumnsDefs());
        }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if entity id or entity type were changed, get new data accordingly
    if ((changes.entityId && changes.entityId.previousValue && changes.entityId.currentValue !== changes.entityId.previousValue) ||
        (changes.entityType && changes.entityType.previousValue && changes.entityType.currentValue !== changes.entityType.previousValue)) {
      this.triggerFetch();
    }
  }

  triggerFetch(): void {
    if (this.records.length === 0) {
      this.getAuditLogsData();
    }
  }

  private getAuditLogsData(): void {
    this.loading = true;
    if (this.entityId || this.entityType) {
      this.auditLogsService.getAllAuditLogsByEntity(this.entityId, this.entityType)
          .then((res: IAuditLog[]) => {
            this.records = this.setTableRecordsByDate(res);
          })
          .catch((error) => {
            this.toastr.error(error.message, 'Error!');
          })
          .finally(() => this.loading = false);
    }
  }

  setTableRecordsByDate(res: IAuditLog[]): any[] {
    // if log action type is update then set each value change in its own row
    const records = [];
    res.forEach(log => {
      const actionDate = this.datePipe(log.actionDate);
      if (log.actionType === ActionTypeEnum.UPDATED && log.oldValue && log.newValue) {
        const logsChanges = Object.keys(log.oldValue);
        logsChanges.forEach(logChangeKey => {
          records.push({
            ...log,
            changedField: logChangeKey ? logChangeKey.charAt(0).toUpperCase() + logChangeKey.slice(1).replace('_', ' ') : '',
            oldValue: this.datePipe(log.oldValue[logChangeKey]),
            newValue: this.datePipe(log.newValue[logChangeKey]),
            actionDate
          });
        });
      } else {
        records.push({...log, changedField: '', oldValue: '', newValue: '', actionDate});
      }
    });
    return records;
  }

  private initColumnsDefs(): Observable<any>[] {
    const tableColumns = [
      {
        headerName: 'AUDIT_LOG_LIST.ACTION_TYPE',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.ACTION_TYPE'),
        field: 'actionType',
        tooltipField: 'actionType',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        filter: true,
        width: 100,
        valueGetter: (params) => {
          if (params?.data?.actionType) {
            return this.translate.instant('AUDIT_LOG_LIST.' + params?.data?.actionType);
          }
        },
      },
      {
        headerName: 'AUDIT_LOG_LIST.PREFORMED_BY',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.PREFORMED_BY'),
        field: 'userDisplayName',
        tooltipField: 'userDisplayName',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        filter: true,
        width: 100
      },
      {
        headerName: 'AUDIT_LOG_LIST.PREFORMED_AT',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.PREFORMED_AT'),
        field: 'actionDate',
        tooltipField: 'actionDate',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        valueFormatter: (params) => {
          return this.lecturesService.getDateStr(params.data.actionDate, this.translate.currentLang);
        },
        comparator: (date1, date2) => {
          return this.dateComparator(date1, date2);
        },
        filter: 'agDateColumnFilter',
        hide: true,
        valueGetter: (lecture: any) => {
          return this.lecturesService.convertToDate(lecture?.data?.created_at);
        }
      },
      {
        headerName: 'AUDIT_LOG_LIST.CHANGED_FIELD',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.CHANGED_FIELD'),
        field: 'changedField',
        tooltipField: 'changedField',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        filter: true,
        width: 100,
      },
      {
        headerName: 'AUDIT_LOG_LIST.OLD_VAL',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.OLD_VAL'),
        field: 'oldValue',
        tooltipField: 'oldValue',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        filter: true,
        width: 100,
      },
      {
        headerName: 'AUDIT_LOG_LIST.NEW_VAL',
        headerTooltip: this.translate.instant('AUDIT_LOG_LIST.NEW_VAL'),
        field: 'newValue',
        tooltipField: 'newValue',
        cellStyle: { textAlign: 'left' },
        sortable: true,
        filter: true,
        width: 100,
      },
    ];

    return tableColumns.map((column) =>
        this.translate.stream(column.headerName).pipe(
            map((headerName: string) => ({
              ...column,
              headerName,
              cellStyle: { textAlign: this.translate.currentLang === LocaleEnum.HE ? 'right' : 'left' },
            })),
            tap(() => this.isRtl = this.translate.currentLang === LocaleEnum.HE)
        )
    );
  }

  private dateComparator(date1, date2): number {
    const date1Number = date1 && new Date(date1).getTime();
    const date2Number = date2 && new Date(date2).getTime();

    if (date1Number == null && date2Number == null) {
      return 0;
    }

    if (date1Number == null) {
      return -1;
    } else if (date2Number == null) {
      return 1;
    }

    return date1Number - date2Number;
  }

  private datePipe = (value: any): string =>
      value && value.seconds ? new Date(parseInt(value.seconds, 0) * 1000).toUTCString() : value

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
